/**
 * BottomNav.tsx — Barra de navegación inferior (fija, compartida en toda la app)
 *
 * Muestra 4 accesos fijos + un botón "Más" (menú desplegable, estilo dropdown)
 * con el resto de opciones. Los accesos cambian según el rol del usuario:
 *
 *   - Administrador: Inicio, Pago Clientes, Instalaciones, Promesa, Más
 *   - Cliente:        Inicio, Pagar Ahora, Facturas, Contraseña/Soporte, Más
 *
 * Se agrega en TODAS las pantallas internas (Menu, Promesa, Tickets, Clientes,
 * ConsultaCliente, FacturasCliente) para que siempre esté visible, igual que AppNavbar.
 */

import React, { useState } from 'react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaHome, FaMoneyCheckAlt, FaTools, FaPlug, FaBars,
  FaFileInvoice, FaKey, FaHeadset, FaUsers, FaMoneyBillWave,
  FaTicketAlt, FaWifi, FaTv, FaUserPlus, FaSignOutAlt, FaPalette, FaReceipt,
} from 'react-icons/fa';
import InstalacionesModal from './InstalacionesModal';
import AyudaModal from './AyudaModal';
import './BottomNav.css';

type NavItem = { key: string; label: string; icon: React.FC; onClick: () => void };

const BottomNav: React.FC = () => {
  const navigate = useNavigate();

  const HomeIcon    = FaHome as unknown as React.FC;
  const MoneyIcon   = FaMoneyCheckAlt as unknown as React.FC;
  const InstallIcon = FaTools as unknown as React.FC;
  const PlugIcon    = FaPlug as unknown as React.FC;
  const MoreIcon    = FaBars as unknown as React.FC;
  const InvoiceIcon = FaFileInvoice as unknown as React.FC;
  const KeyIcon     = FaKey as unknown as React.FC;
  const HeadsetIcon = FaHeadset as unknown as React.FC;
  const UsersIcon   = FaUsers as unknown as React.FC;
  const AbonoIcon   = FaMoneyBillWave as unknown as React.FC;
  const TicketIcon  = FaTicketAlt as unknown as React.FC;
  const WifiIcon    = FaWifi as unknown as React.FC;
  const TvIcon      = FaTv as unknown as React.FC;
  const SellerIcon  = FaUserPlus as unknown as React.FC;
  const LogoutIcon  = FaSignOutAlt as unknown as React.FC;
  const TemaIcon    = FaPalette as unknown as React.FC;
  const ReceiptIcon = FaReceipt as unknown as React.FC;

  const clientDataJSON = localStorage.getItem('clientData');
  const clientData = clientDataJSON ? JSON.parse(clientDataJSON) : null;

  const [showMoreSheet, setShowMoreSheet]   = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showAyudaModal, setShowAyudaModal]     = useState(false);
  const [showProximamente, setShowProximamente] = useState(false);
  const [proximamenteOpcion, setProximamenteOpcion] = useState('');

  const handleCloseInstall = () => { setShowInstallModal(false); window.location.reload(); };
  const handleProximamente = (opcion: string) => { setProximamenteOpcion(opcion); setShowProximamente(true); };

  const handleLogout = () => {
    localStorage.removeItem('clientData');
    navigate('/');
  };

  // Si no hay sesión iniciada, no mostramos la barra
  if (!clientData) return null;

  const showAdmin = Boolean(clientData.token_cliente?.trim());

  // Clave del campo personalizado donde se guarda el OID de GenieACS (con el que se consulta el servicio)
  const CLAVE_OID_GENIEACS = '1770612677';
  const tieneOidGenieAcs = (srv: any): boolean => {
    try {
      const personalizados = typeof srv.personalizados === 'string' ? JSON.parse(srv.personalizados) : (srv.personalizados || {});
      return Boolean(personalizados[CLAVE_OID_GENIEACS]);
    } catch { return false; }
  };
  const showChangePwd = Boolean(clientData.servicios?.some(tieneOidGenieAcs));

  const adminNavItems: NavItem[] = [
    { key: 'inicio',        label: 'Inicio',        icon: HomeIcon,    onClick: () => navigate('/menu') },
    { key: 'pagoClientes',  label: 'Pago Clientes', icon: MoneyIcon,   onClick: () => navigate('/menu/pagoclientes') },
    { key: 'instalaciones', label: 'Instalaciones', icon: InstallIcon, onClick: () => setShowInstallModal(true) },
    { key: 'promesa',       label: 'Promesa',       icon: PlugIcon,    onClick: () => navigate('/menu/promesa') },
  ];

  const clientNavItems: NavItem[] = [
    { key: 'inicio', label: 'Inicio', icon: HomeIcon, onClick: () => navigate('/menu') },
    { key: 'pagar',  label: 'Pagar Ahora', icon: MoneyIcon, onClick: () => navigate(`/r/${btoa(clientData.cedula)}`) },
    { key: 'facturas', label: 'Facturas', icon: InvoiceIcon, onClick: () => navigate('/clientes/facturas') },
    showChangePwd
      ? { key: 'clave', label: 'Contraseña', icon: KeyIcon, onClick: () => navigate('/menu/wifi') }
      : { key: 'soporte', label: 'Soporte', icon: HeadsetIcon, onClick: () => setShowAyudaModal(true) },
  ];

  const navItems: NavItem[] = showAdmin ? adminNavItems : clientNavItems;

  // Elegir tema claro/oscuro. Lo ve TODO el mundo, cliente o administrador.
  const aparienciaItem: NavItem = {
    key: 'apariencia', label: 'Apariencia', icon: TemaIcon, onClick: () => navigate('/menu/apariencia'),
  };

  const promoInternetItem: NavItem = {
    key: 'promoNet', label: 'Promociones Internet', icon: WifiIcon, onClick: () => navigate('/menu/promociones-internet'),
  };

  const moreItems: NavItem[] = showAdmin
    ? [
        { key: 'clientes', label: 'Mis Clientes',        icon: UsersIcon,  onClick: () => navigate('/menu/clientes') },
        { key: 'abono',    label: 'Abono a Factura',      icon: AbonoIcon,  onClick: () => navigate('/menu/abono') },
        { key: 'comprobantes', label: 'Subir Comprobante', icon: ReceiptIcon, onClick: () => navigate('/menu/comprobantes') },
        { key: 'tickets',  label: 'Mis Tickets',          icon: TicketIcon, onClick: () => navigate('/menu/tickets') },
        { key: 'pagar',    label: 'Pagar Ahora',          icon: MoneyIcon,  onClick: () => navigate(`/r/${btoa(clientData.cedula)}`) },
        ...(showChangePwd
          ? [{ key: 'clave', label: 'Cambiar Contraseña', icon: KeyIcon, onClick: () => navigate('/menu/wifi') } as NavItem]
          : []),
        { key: 'soporte',  label: 'Soporte Técnico',      icon: HeadsetIcon, onClick: () => setShowAyudaModal(true) },
        aparienciaItem,
        promoInternetItem,
        { key: 'promoTv',  label: 'Promociones TV',       icon: TvIcon,     onClick: () => handleProximamente('Promociones TV') },
        { key: 'vendedor', label: 'Quiero ser vendedor',  icon: SellerIcon, onClick: () => handleProximamente('Quiero ser vendedor') },
      ]
    : [
        ...(showChangePwd
          ? [{ key: 'soporte', label: 'Soporte Técnico', icon: HeadsetIcon, onClick: () => setShowAyudaModal(true) } as NavItem]
          : []),
        aparienciaItem,
        promoInternetItem,
        { key: 'promoTv',  label: 'Promociones TV',       icon: TvIcon,   onClick: () => handleProximamente('Promociones TV') },
        { key: 'vendedor', label: 'Quiero ser vendedor',  icon: SellerIcon, onClick: () => handleProximamente('Quiero ser vendedor') },
      ];

  const handleNavItemClick = (item: NavItem) => {
    setShowMoreSheet(false);
    item.onClick();
  };

  return (
    <>
      <div className="bn-bar">
        {navItems.map(item => {
          const Icon = item.icon;
          return (
            <button key={item.key} onClick={item.onClick} className="bn-item">
              <span className="bn-item-icon"><Icon /></span>
              <span>{item.label}</span>
            </button>
          );
        })}

        <Dropdown drop="up" align="end" show={showMoreSheet} onToggle={setShowMoreSheet} style={{ flex: 1 }}>
          <Dropdown.Toggle as="button" id="bn-more-toggle" className="bn-item bn-more-toggle-btn">
            <span className="bn-item-icon"><MoreIcon /></span>
            <span>Más</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="bn-more-menu">
            <div className="bn-more-title">Todas las opciones</div>
            {moreItems.map(item => {
              const Icon = item.icon;
              return (
                <Dropdown.Item
                  key={item.key}
                  onClick={() => handleNavItemClick(item)}
                  className="bn-more-item"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}
                >
                  <span className="bn-more-item-icon"><Icon /></span>
                  <span style={{ fontSize: 14, color: 'var(--sn-texto)' }}>{item.label}</span>
                </Dropdown.Item>
              );
            })}
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={handleLogout}
              className="bn-more-item bn-more-item--danger"
              style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}
            >
              <span className="bn-more-item-icon bn-more-item-icon--danger"><LogoutIcon /></span>
              <span style={{ fontSize: 14, color: 'var(--sn-peligro)' }}>Cerrar sesión</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Espaciador para que el contenido de la página no quede tapado */}
      <div className="bn-spacer" aria-hidden="true" />

      <InstalacionesModal show={showInstallModal} onHide={handleCloseInstall} />
      <AyudaModal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} />

      <Modal show={showProximamente} onHide={() => setShowProximamente(false)} centered>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: 72 }}>🚀</div>
          <h4 className="mt-3 fw-bold">¡Próximamente!</h4>
          <p className="text-muted mt-2">
            Estamos trabajando en la opción<br />
            <strong>{proximamenteOpcion}</strong><br />
            para ofrecerte la mejor experiencia.<br />
            ¡Muy pronto disponible!
          </p>
          <div style={{ fontSize: 24 }}>⚙️ 🛠️ ✨</div>
          <Button variant="primary" className="mt-3 px-4" onClick={() => setShowProximamente(false)}>
            Entendido
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BottomNav;
